import { useEffect, useRef, useState } from 'react';
import styles from './WinampPlayer.module.css';
import content from '../../data/content.json';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const { winamp } = content.home;

const COMPACT_QUERY = '(max-width: 767px)';

/* Winamp's windows are a fixed 275px wide and 116px tall each, so the default
   three-window stack is 348px — taller than a phone in landscape. On small
   screens we drop the equalizer and keep main + playlist (232px), which still
   lets you see and pick tracks. Offsets are from the top-left of an imaginary
   box that Webamp centres over the anchor node. Every window listed needs a
   `position`, even a closed one — Webamp reads it unconditionally. */
const COMPACT_LAYOUT = {
  main: { position: { top: 0, left: 0 } },
  playlist: { position: { top: 116, left: 0 } },
  equalizer: { position: { top: 232, left: 0 }, closed: true },
};

const WINDOW_IDS = ['main-window', 'equalizer-window', 'playlist-window'];
const TASKBAR_HEIGHT = 40;
const DOCK_GAP = 12;

/** Viewport-space bounding box of every open Winamp window, or null if none. */
const getPlayerBox = () => {
  const rects = WINDOW_IDS
    .map((id) => document.getElementById(id))
    .filter(Boolean)
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);

  if (!rects.length) return null;
  return {
    left: Math.min(...rects.map((r) => r.left)),
    top: Math.min(...rects.map((r) => r.top)),
    right: Math.max(...rects.map((r) => r.right)),
    bottom: Math.max(...rects.map((r) => r.bottom)),
  };
};

/**
 * WinampPlayer — a real Winamp 2 (via webamp) launched from an XP-style chip.
 *
 * The webamp bundle (~large) is dynamically imported only when the launcher is
 * clicked, so it costs nothing on page load. Webamp renders draggable windows
 * centred on an invisible zero-size anchor at the viewport centre; closing it
 * from Winamp's own menu disposes the instance and resets the launcher.
 *
 * Tracks live in content.json under home.winamp.tracks — `url` for absolute
 * URLs, `file` for paths relative to /public (e.g. "audio/set.mp3"). Give each
 * one a `duration` in seconds: without it Webamp spins up a hidden <audio> per
 * track just to measure length, which downloads every file on open.
 *
 * Webamp's windows are absolutely positioned in the document, so by default
 * they scroll away with the page like real desktop furniture. The launcher
 * stays mounted while the player is open and toggles docking: docked, the
 * player pins to a viewport slot above the taskbar and rides along as you
 * scroll; undocked, it drops back into the page where it sits.
 *
 * @param {('floating'|'tray')} [variant='floating'] - 'floating' is the
 *   bottom-right chip; 'tray' is a compact icon button for the Taskbar tray.
 */
const WinampPlayer = ({ variant = 'floating' }) => {
  const [status, setStatus] = useState('closed'); // closed | loading | open
  const [docked, setDocked] = useState(false);
  const anchorRef = useRef(null);
  const webampRef = useRef(null);
  const isCompact = useMediaQuery(COMPACT_QUERY);

  const launch = async () => {
    if (status !== 'closed') return;
    setStatus('loading');

    const { default: Webamp } = await import('webamp');
    if (!Webamp.browserIsSupported()) {
      setStatus('closed');
      return;
    }

    const webamp = new Webamp({
      initialTracks: winamp.tracks.map((track) => ({
        metaData: { artist: track.artist, title: track.title },
        duration: track.duration,
        url: track.file ? `${process.env.PUBLIC_URL}/${track.file}` : track.url,
      })),
      ...(isCompact ? { windowLayout: COMPACT_LAYOUT } : {}),
      zIndex: 3000,
    });
    webampRef.current = webamp;

    webamp.onClose(() => {
      webamp.dispose();
      webampRef.current = null;
      setDocked(false);
      setStatus('closed');
    });

    await webamp.renderWhenReady(anchorRef.current);
    setStatus('open');
  };

  /**
   * Dock: pin the player to a slot in the viewport so it survives scrolling.
   *
   * Webamp lays its windows out as `translate(x, y)` inside a `#webamp`
   * container that sits at the document origin, so their on-screen position is
   * `container offset + translate - scroll`. Switching the container to
   * `fixed` drops the scroll term; offsetting it by the gap between where the
   * group is and where we want it slides the whole group into the slot without
   * touching Webamp's own state, so dragging still works while docked.
   */
  const dock = () => {
    const node = document.getElementById('webamp');
    const box = getPlayerBox();
    if (!node || !box) return;

    const width = box.right - box.left;
    const height = box.bottom - box.top;
    // Centred on phones, right-hand corner on desktop; never off the left edge
    // if the user has dragged the windows apart into something very wide
    const slotLeft = Math.max(DOCK_GAP, isCompact
      ? (window.innerWidth - width) / 2
      : window.innerWidth - width - DOCK_GAP);
    const slotTop = Math.max(DOCK_GAP, window.innerHeight - TASKBAR_HEIGHT - DOCK_GAP - height);

    // Recover Webamp's own translate offsets from the rendered box, so the slot
    // is computed the same way whether we're docking fresh or re-docking after
    // a resize. Only an undocked container carries the scroll term.
    const isFixed = node.style.position === 'fixed';
    const curLeft = parseFloat(node.style.left) || 0;
    const curTop = parseFloat(node.style.top) || 0;
    const translateX = box.left - curLeft + (isFixed ? 0 : window.scrollX);
    const translateY = box.top - curTop + (isFixed ? 0 : window.scrollY);

    node.style.position = 'fixed';
    node.style.left = `${slotLeft - translateX}px`;
    node.style.top = `${slotTop - translateY}px`;
    setDocked(true);
  };

  /** Release the player back into the page, leaving it exactly where it looks. */
  const undock = () => {
    const node = document.getElementById('webamp');
    if (!node) return;

    node.style.position = 'absolute';
    node.style.left = `${(parseFloat(node.style.left) || 0) + window.scrollX}px`;
    node.style.top = `${(parseFloat(node.style.top) || 0) + window.scrollY}px`;
    setDocked(false);
  };

  // Keep a docked player in its slot when the viewport changes (rotation, chrome
  // showing and hiding, desktop resize)
  useEffect(() => {
    if (!docked) return undefined;
    const onResize = () => dock();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docked, isCompact]);

  // Dispose if the page unmounts while Webamp is open
  useEffect(() => () => webampRef.current?.dispose(), []);

  const isOpen = status === 'open';
  let label = `Open ${winamp.launcher_label}`;
  if (isOpen) label = docked ? winamp.undock_label : winamp.dock_label;

  return (
    <>
      <button
        type="button"
        className={[
          variant === 'tray' ? styles.trayLauncher : styles.launcher,
          docked ? styles.launcherActive : '',
        ].join(' ')}
        onClick={isOpen ? (docked ? undock : dock) : launch}
        disabled={status === 'loading'}
        title={label}
        aria-label={label}
      >
        <span className={styles.launcherIcon} aria-hidden="true">
          {status === 'loading' ? '⌛' : '♫'}
        </span>
        {variant !== 'tray' && (status === 'loading' ? 'loading…' : winamp.launcher_label)}
      </button>
      <div ref={anchorRef} className={styles.anchor} aria-hidden="true" />
    </>
  );
};

export default WinampPlayer;
